import type { RunGradeResult } from '../game/runGrades';

const STORAGE_KEY = 'last-mile-driver-progress-v1';

export type DriverProgress = {
  totalXp: number;
  runsCompleted: number;
  bestGradeScore: number;
};

export type DriverProgressResult = {
  before: DriverProgress;
  after: DriverProgress;
  xpEarned: number;
  rankBefore: number;
  rankAfter: number;
  rankTitle: string;
  currentRankXp: number;
  nextRankXp: number;
  progress01: number;
  leveledUp: boolean;
};

export type DriverProgressRunInput = {
  grade: RunGradeResult;
  stylePoints: number;
  takedowns: number;
  pursuitBreakers: number;
  nearMisses: number;
  completed: boolean;
};

const DEFAULT_PROGRESS: DriverProgress = {
  totalXp: 0,
  runsCompleted: 0,
  bestGradeScore: 0,
};

function load(): DriverProgress {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_PROGRESS };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PROGRESS };
    const parsed = JSON.parse(raw) as Partial<DriverProgress>;
    return {
      totalXp: Math.max(0, Math.floor(Number(parsed.totalXp) || 0)),
      runsCompleted: Math.max(0, Math.floor(Number(parsed.runsCompleted) || 0)),
      bestGradeScore: Math.max(0, Math.floor(Number(parsed.bestGradeScore) || 0)),
    };
  } catch {
    return { ...DEFAULT_PROGRESS };
  }
}

function save(progress: DriverProgress): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch {
    /* quota / private mode */
  }
}

export function rankForXp(totalXp: number): number {
  return Math.max(1, Math.floor(Math.sqrt(Math.max(0, totalXp) / 420)) + 1);
}

export function xpForRank(rank: number): number {
  const r = Math.max(1, Math.floor(rank));
  return Math.floor((r - 1) * (r - 1) * 420);
}

export function rankTitle(rank: number): string {
  if (rank >= 18) return 'Ghost Driver';
  if (rank >= 14) return 'Black Route Ace';
  if (rank >= 10) return 'Pursuit Specialist';
  if (rank >= 7) return 'Heat Runner';
  if (rank >= 4) return 'Night Courier';
  return 'Street Rookie';
}

export function calculateRunXp(input: DriverProgressRunInput): number {
  const completion = input.completed ? 220 : 0;
  const gradeXp = input.grade.score * 7;
  const styleXp = Math.min(340, Math.floor(input.stylePoints / 18));
  const eventXp =
    input.takedowns * 60 + input.pursuitBreakers * 95 + input.nearMisses * 28;
  const gradeBonus =
    input.grade.grade === 'SS'
      ? 320
      : input.grade.grade === 'S'
        ? 210
        : input.grade.grade === 'A'
          ? 120
          : input.grade.grade === 'B'
            ? 55
            : 0;
  return Math.max(0, Math.floor(completion + gradeXp + styleXp + eventXp + gradeBonus));
}

export function recordDriverProgress(input: DriverProgressRunInput): DriverProgressResult {
  const before = load();
  const rankBefore = rankForXp(before.totalXp);
  const xpEarned = calculateRunXp(input);
  const after: DriverProgress = {
    totalXp: before.totalXp + xpEarned,
    runsCompleted: before.runsCompleted + (input.completed ? 1 : 0),
    bestGradeScore: Math.max(before.bestGradeScore, input.grade.score),
  };
  save(after);

  const rankAfter = rankForXp(after.totalXp);
  const currentRankXp = xpForRank(rankAfter);
  const nextRankXp = xpForRank(rankAfter + 1);
  const denom = Math.max(1, nextRankXp - currentRankXp);

  return {
    before,
    after,
    xpEarned,
    rankBefore,
    rankAfter,
    rankTitle: rankTitle(rankAfter),
    currentRankXp,
    nextRankXp,
    progress01: Math.max(0, Math.min(1, (after.totalXp - currentRankXp) / denom)),
    leveledUp: rankAfter > rankBefore,
  };
}

export function getDriverProgress(): DriverProgress {
  return load();
}
